import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import createPayment from '@/shared/api/orders/payments/createPayment';
import getPaymentMethods from '@/shared/api/orders/payments/getPaymentMethods';
import getPaymentStatuses from '@/shared/api/orders/payments/getPaymentStatuses';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import DialogName from '@/shared/types/dialogName.enum';
import { PaymentCreate } from '@/shared/types/order/payment.type';

type OnUpdateDialogState = ReturnType<
  typeof useModalDialog<unknown, DialogName>
>['onUpdateDialogState'];

type OnUpdateNotificationBar = ReturnType<
  typeof useNotificationBar
>['onUpdateNotificationBar'];

type Args = {
  resetState: boolean;
  onUpdateDialogState: OnUpdateDialogState;
  onUpdateNotificationBar: OnUpdateNotificationBar;
  setRefetchOrder?: (value: boolean) => void;
};

const useCreatePayment = ({
  resetState,
  onUpdateDialogState,
  onUpdateNotificationBar,
  setRefetchOrder,
}: Args) => {
  const { t } = useTranslation(['payments', 'common']);
  const redirectLogin = useRedirectLogin();

  const [paymentStatuses, setPaymentStatuses] = useState<string[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<string[]>([]);

  const [paymentFormData, setPaymentFormData] = useState<PaymentCreate>({
    in_order_id: '',
    transaction_amount: '',
    payment_method: '',
    status: '',
  });
  const [paymentFormErrors, setPaymentFormErrors] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    const fetchPaymentStatuses = async () => {
      const response = await getPaymentStatuses({
        onUnauthorized: redirectLogin,
      });

      if (response.success) {
        const legacyStatuses = ['failed', 'succeeded'];
        const filteredStatuses = response.data.filter(
          (status) => !legacyStatuses.includes(status),
        );

        setPaymentStatuses(filteredStatuses);
      }
    };

    void fetchPaymentStatuses();
  }, [redirectLogin]);

  useEffect(() => {
    const fetchPaymentMethods = async () => {
      const response = await getPaymentMethods({
        onUnauthorized: redirectLogin,
      });

      if (response.success) {
        setPaymentMethods(response.data);
      }
    };

    void fetchPaymentMethods();
  }, [redirectLogin]);

  useEffect(() => {
    if (resetState) {
      setPaymentFormData({
        in_order_id: '',
        transaction_amount: '',
        payment_method: '',
        status: '',
      });
      setPaymentFormErrors({});
    }
  }, [resetState]);

  const onValidatePaymentFormField = (fieldName: string) => {
    const validationConfig: {
      [key: string]: {
        required: boolean;
        errorMessage: string;
      };
    } = {
      transaction_amount: {
        required: true,
        errorMessage: 'payments:transaction_amount_required',
      },
      payment_method: {
        required: true,
        errorMessage: 'payments:payment_method_required',
      },
      status: {
        required: true,
        errorMessage: 'payments:status_required',
      },
    };

    const errors: Record<string, string> = { ...paymentFormErrors };

    const indexKey = fieldName as keyof PaymentCreate;
    const valueToValidate = paymentFormData[indexKey];
    const fieldConfig = validationConfig[fieldName];

    if (fieldConfig) {
      if (fieldConfig.required && !valueToValidate) {
        errors[indexKey] = t(fieldConfig.errorMessage);
      } else {
        errors[indexKey] = '';
      }
    }

    setPaymentFormErrors(errors);
    return errors;
  };

  const onValidatePaymentForm = () => {
    const errors: Record<string, string> = {};

    const fieldsToValidate = ['transaction_amount', 'payment_method', 'status'];

    fieldsToValidate.forEach((field) => {
      const fieldErrors = onValidatePaymentFormField(field);
      errors[field] = fieldErrors[field];
    });

    setPaymentFormErrors(errors);
    return errors;
  };

  const handleCreatePayment = async (values: PaymentCreate) => {
    onUpdateDialogState(DialogName.CreatePayment, { loading: true });

    const response = await createPayment({
      body: values,
      onUnauthorized: redirectLogin,
    });

    if (response.success) {
      onUpdateDialogState(DialogName.CreatePayment, {
        loading: false,
        open: false,
      });
      onUpdateNotificationBar({
        message: t('payments:paymentCreated'),
        severity: 'success',
        open: true,
      });
      setRefetchOrder?.(true);
    } else {
      onUpdateDialogState(DialogName.CreatePayment, {
        loading: false,
        errors: [response.msg],
      });
    }
  };

  return {
    paymentStatuses,
    paymentMethods,
    paymentFormData,
    paymentFormErrors,
    setPaymentFormData,
    handleCreatePayment,
    onValidatePaymentFormField,
    onValidatePaymentForm,
  };
};

export default useCreatePayment;
